<template>
    <vue-scroll>
        <div class="page-header">
            <h1>Pending Orders</h1>
        </div>
        <div class="float-left">
            Actions
            <el-select
                style="width: auto"
                v-model="action.name"
                placeholder="Select Action"
                :disabled="!action.orders.length"
            >
                <el-option
                    :key="index"
                    :label="action.name"
                    :value="action.value"
                    v-for="(action, index) in actions"
                >
                </el-option>
            </el-select>
            <el-select
                style="width: auto"
                v-model="action.assignee_id"
                v-if="action.name == 'assignee'"
                placeholder="Select User"
            >
                <el-option
                    :key="user.id"
                    :label="user.name"
                    :value="user.id"
                    v-for="user in assignableUsers"
                >
                </el-option>
            </el-select>
            <el-button :disabled="!action.orders.length" @click="applyAction"
                >Apply</el-button
            >
            <el-input
                @input="onInputSearch"
                placeholder="Search Orders..."
                style="width: 250px"
                v-model="filter.search"
            />
        </div>
        <div class="clearfix"></div>
        <el-table
            :data="orders.data"
            :row-class-name="tableRowClassName"
            @sort-change="onSortChange"
            @selection-change="handleSelectionChange"
            class="mt-20"
            ref="table"
            row-key="id"
            v-loading="orders.meta.loading"
        >
            <el-table-column type="selection" width="55"> </el-table-column>
            <el-table-column width="30">
                <template v-slot="{ row }">
                    <span
                        v-if="isOrderDue(row)"
                        class="el-icon-warning-outline"
                    ></span>
                </template>
            </el-table-column>
            <el-table-column
                label="Order #"
                prop="id"
                sortable="custom"
                width="125"
            >
                <template v-slot="scope">
                    {{ scope.row.id }}
                </template>
            </el-table-column>
            <el-table-column
                label="Customer"
                prop="community"
                sortable="custom"
            >
                <template v-slot="{ row }">
                    {{ row.tenant.name }}
                </template>
            </el-table-column>
            <el-table-column
                label="Order Placed"
                prop="created_at"
                sortable="custom"
                width="200"
            >
                <template v-slot="{ row }">
                    {{ row.created_at | florida_short_datetime }}
                </template>
            </el-table-column>
            <el-table-column
                label="Issue"
                prop="issue_month"
                sortable="custom"
                width="150"
            >
                <template v-slot="{ row }">
                    {{ row.formatted_month }}
                </template>
            </el-table-column>
            <el-table-column
                label="Status"
                prop="formatted_status"
                sortable="custom"
                width="200"
            />
            <el-table-column
                label="Rush Processing"
                prop="rush_processing"
                sortable="custom"
                width="175"
            >
                <template v-slot="{ row }">
                    {{ row.rush_processing ? "Yes" : "No" }}
                </template>
            </el-table-column>
            <el-table-column
                label="Approval"
                prop="approval_status"
                sortable="custom"
                width="120px"
            >
                <template v-slot="{ row }">
                    <el-tag
                        type="success"
                        v-if="row.approval_status === ApprovalStatus.APPROVED"
                        ><i class="el-icon-check" /> Approved
                    </el-tag>
                    <el-tag
                        type="danger"
                        v-else-if="
                            row.approval_status === ApprovalStatus.REJECTED
                        "
                        ><i class="mdi mdi-close" /> Rejected
                    </el-tag>
                    <el-tag type="warning" v-else-if="row.is_billable_edit">
                        Edit
                    </el-tag>
                    <el-tag type="danger" v-else>Pending</el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="actions" width="130px">
                <template v-slot="{ row }">
                    <el-button-group>
                        <router-link
                            :to="{
                                name: Routes.VIEW_ORDER,
                                params: { id: row.id },
                            }"
                            class="el-button el-button--default text-decoration-none"
                        >
                            <i class="mdi mdi-eye"></i>
                            View
                        </router-link>
                    </el-button-group>
                </template>
            </el-table-column>
        </el-table>
        <app-pagination
            :pagination="orders.meta.pagination"
            @current-change="onCurrentPageChange"
            class="mt-10"
        />
    </vue-scroll>
</template>

<script>
import * as ApprovalStatus from "@/constants/approval-status";
import formatters from "@/mixins/table/formatters";
import OrderUtils from "@/utils/order";
import qs from "qs";
import backend_paginator from "@/mixins/backend-paginator";
import * as Routes from "@/constants/routes";
import * as Roles from "@/constants/roles";
import * as Permissions from "@/constants/permissions";
import auth from "@/mixins/auth";

const INCLUDE = "tenant";

function getData(params = {}) {
    _.extend(params, {
        include: INCLUDE,
        pending_page: 1,
        rush_processing_first: 1,
    });
    return axios.get(`/web-api/order?${qs.stringify(params)}`);
}

export default {
    mixins: [formatters, backend_paginator, auth],
    beforeRouteEnter(to, from, next) {
        getData(to.query).then((response) => {
            next((vm) => {
                _.extend(
                    vm.currentQuery,
                    _.pick(to.query, ["sort_by", "sort_order", "page"])
                );
                _.extend(vm.currentQuery.filter, _.get(to, "query.filter"));
                vm.setData(response);
            });
        });
    },
    beforeRouteUpdate(to, from, next) {
        this.orders.meta.loading = true;
        getData(to.query)
            .then((response) => {
                this.setData(response);
                next();
            })
            .finally(() => (this.orders.meta.loading = false));
    },
    data() {
        return {
            ApprovalStatus,
            Routes,
            dialogVisible: false,
            form: new Form({
                name: null,
                slug: null,
                logo: null,
                location: {
                    street: null,
                    city: null,
                    state: null,
                    zip: null,
                },
            }),
            filter: {
                created_at: null,
                search: "",
            },
            currentQuery: {
                filter: {
                    search: null,
                    created_at: null,
                },
                page: 1,
                sort_by: null,
                sort_order: "asc",
            },
            routeName: Routes.PENDING_ORDERS,
            logo_src: null,
            editingItem: null,
            orders: {
                data: [],
                meta: {
                    pagination: null,
                    loading: false,
                },
            },
            actions: [
                {
                    name: "Approve",
                    value: "approve",
                },
                {
                    name: "Mark as picked up",
                    value: "picked up",
                },
                {
                    name: "Assigned To",
                    value: "assignee",
                },
            ],
            action: {
                name: null,
                assignee_id: null,
                orders: [],
            },
        };
    },
    computed: {
        assignableUsers() {
            let users = _.filter(this.$store.state.users.data, (user) => {
                if (user.is_assignable) {
                    return !!_.find(user.roles.data, (role) =>
                        _.includes(Roles.MAIN_ROLES, role.role)
                    );
                }
            });
            users = _.concat(users, this.$store.state.user);
            return _.sortBy(users, "name");
        },
    },
    mounted() {
        this.filter.search = _.get(this.$route, "query.filter.search", "");
        Vue.nextTick(() => {
            this.updateTableSorting();
        });
        if (this.can(Permissions.UPDATE_INVOICED_ORDER_STATUS)) {
            this.actions.push({
                name: "Mark as invoiced",
                value: "invoiced",
            });
        }
    },
    methods: {
        onInputSearch: _.debounce(function () {
            this.currentQuery.page = 1;
            this.currentQuery.filter.search = this.filter.search;
            this.updateRoute();
        }, 500),
        setData(response) {
            this.orders.data = response.data.data;
            this.orders.meta.pagination = response.data.meta.pagination;
        },
        isOrderDue(order) {
            return OrderUtils.isDue(order);
        },
        tableRowClassName({ row, rowIndex }) {
            if (row.rush_processing) {
                if (OrderUtils.isDue(row)) {
                    return "rush-row-error";
                } else {
                    return "rush-row";
                }
            }
            if (OrderUtils.isDue(row)) {
                return "error-row";
            }
            return "";
        },
        handleSelectionChange(val) {
            this.action.orders = val;
        },
        applyAction() {
            let vm = this;
            if (!vm.action.orders.length) {
                vm.$notify.error({
                    message: "Please select orders for apply action.",
                });
                return;
            }
            if (!vm.action.name) {
                vm.$notify.error({
                    message: "Please select action for apply action.",
                });
                return;
            }
            if (vm.action.name == "assignee" && !vm.action.assignee_id) {
                vm.$notify.error({
                    message: "Please select user for apply action.",
                });
                return;
            }
            if (vm.action.name == "invoiced") {
                let not_approved_orders = vm.action.orders.filter((o) => {
                    return o.approval_status != "approved";
                });
                if (not_approved_orders.length > 0) {
                    vm.$notify.warning({
                        message: `NOTE: The following orders will NOT be marked as invoiced: ${not_approved_orders
                            .map((o) => o.id)
                            .join()}`,
                    });
                }
            }

            axios
                .post("/web-api/apply-action/orders", vm.action)
                .then((response) => {
                    //console.log(response);
                    vm.updateRoute();
                    vm.$notify.success({
                        message: "Orders updated successfully.",
                    });
                })
                .catch((error) => {
                    console.log(error);
                    vm.orders.meta.loading = false;
                });
        },
    },
};
</script>

<style>
.select-light-grey .el-input__inner {
    background-color: #f1f1f1;
}
</style>
